import React, { useCallback, useEffect, useState } from 'react';

import { Badge, Group, Text, Title, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconArchive, IconArchiveOff, IconChartBar, IconPencil, IconUsers } from '@tabler/icons-react';
import Menu from 'components/Menu';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import useArchiveDiscount from 'hooks/discount/useArchiveDiscount';
import useDiscounts from 'hooks/discount/useDiscounts';
import useUnarchiveDiscount from 'hooks/discount/useUnarchiveDiscount';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { CellProps } from 'react-table';
import { DiscountList } from 'types';
import notificationHandler from 'utils/notifications';

import DrawerDiscountForm from './DrawerDiscountForm';
import DrawerDiscountUsageReport from './DrawerDiscountUsageReport';

type DiscountListRow = DiscountList & { totalUses?: number };

const getStatusFilters = (
  statuses: string[],
): { isActive?: boolean; isInactive?: boolean; isArchived?: boolean } => ({
  isActive: statuses.includes('active') || undefined,
  isInactive: statuses.includes('inactive') || undefined,
  isArchived: statuses.includes('archived') || undefined,
});

const DiscountsTableContainer = ({
  searchTerm,
  statuses = [],
  permission = true,
}: {
  searchTerm: string;
  statuses?: string[];
  permission?: boolean;
}): JSX.Element => {
  const theme = useMantineTheme();
  const { formatCurrency } = useLocalization();

  const [ordering, setOrdering] = useState('name');
  const [ascending, setAscending] = useState(true);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useDiscounts({
      search: searchTerm,
      ordering: ascending ? ordering : `-${ordering}`,
      ...getStatusFilters(statuses),
    });

  const archiveMutation = useArchiveDiscount();
  const unarchiveMutation = useUnarchiveDiscount();
  const { trackDiscountArchived, trackDiscountUnarchived } = useStaffTracking();

  const [tableData, setTableData] = useState<DiscountListRow[]>([]);
  const [selectedDiscount, setSelectedDiscount] = useState<DiscountListRow>();
  const [isEditFormOpen, { open: openEditForm, close: closeEditForm }] = useDisclosure(false);
  const [selectedReportDiscount, setSelectedReportDiscount] = useState<DiscountListRow>();
  const [isReportOpen, { open: openReport, close: closeReport }] = useDisclosure(false);

  const handleCloseReport = (): void => {
    closeReport();
    setSelectedReportDiscount(undefined);
  };

  const handleOpenReport = (discount: DiscountListRow): void => {
    setSelectedReportDiscount(discount);
    openReport();
  };

  const handleHeaderClick = useCallback(
    (header: string): void => {
      setOrdering(header);
      setAscending(!ascending);
    },
    [ascending],
  );

  const handleCloseEditForm = (): void => {
    setSelectedDiscount(undefined);
    closeEditForm();
  };

  const handleEdit = (discount: DiscountListRow): void => {
    setSelectedDiscount(discount);
    openEditForm();
  };

  const handleArchive = (discount: DiscountListRow): void => {
    const { successNotification, errorNotification } = notificationHandler();
    archiveMutation.mutate(discount.discountId, {
      onSuccess: () => {
        successNotification('Discount archived', `${discount.name} has been archived.`);
        trackDiscountArchived(discount.discountId, discount.name);
      },
      onError: error => {
        errorNotification('Archive failed', error.response?.data.message);
      },
    });
  };

  const handleUnarchive = (discount: DiscountListRow): void => {
    const { successNotification, errorNotification } = notificationHandler();
    unarchiveMutation.mutate(discount.discountId, {
      onSuccess: () => {
        successNotification('Discount unarchived', `${discount.name} has been restored.`);
        trackDiscountUnarchived(discount.discountId, discount.name);
      },
      onError: error => {
        errorNotification('Unarchive failed', error.response?.data.message);
      },
    });
  };

  const formatValue = (discount: DiscountListRow): string => {
    if (discount.valueType === 'PERCENTAGE' && discount.percentage != null) {
      return `${(parseFloat(discount.percentage) * 100).toFixed(2)}%`;
    }
    if (discount.valueType === 'FIXED_AMOUNT' && discount.amount != null) {
      return formatCurrency(discount.amount / 100);
    }
    return '—';
  };

  const getOpacity = (discount: DiscountListRow): number => (discount.deletedAt ? 0.5 : 1);

  const columns = [
    {
      Header: 'Name',
      accessor: 'name',
      Cell: ({ row }: CellProps<DiscountListRow>) => (
        <Text
          onClick={() => handleEdit(row.original)}
          weight='bold'
          underline
          sx={{ cursor: 'pointer', width: 'fit-content', opacity: getOpacity(row.original) }}
        >
          {row.original.name}
        </Text>
      ),
    },
    {
      Header: 'Type',
      accessor: 'valueType',
      Cell: ({ row }: CellProps<DiscountListRow>) => (
        <Text sx={{ opacity: getOpacity(row.original) }}>
          {row.original.valueType === 'PERCENTAGE' ? 'Percentage' : 'Fixed Amount'}
        </Text>
      ),
    },
    {
      Header: 'Value',
      id: 'value',
      Cell: ({ row }: CellProps<DiscountListRow>) => (
        <Text sx={{ opacity: getOpacity(row.original) }}>{formatValue(row.original)}</Text>
      ),
    },
    {
      Header: 'Code',
      accessor: 'code',
      Cell: ({ row }: CellProps<DiscountListRow>) => (
        <Text sx={{ fontFamily: 'monospace', opacity: getOpacity(row.original) }}>{row.original.code}</Text>
      ),
    },
    {
      Header: 'Limits',
      accessor: 'isOneTimeUse',
      Cell: ({ row }: CellProps<DiscountListRow>) => (
        <Badge
          color={row.original.isOneTimeUse ? 'blue' : 'yellow'}
          variant='light'
          sx={{ opacity: getOpacity(row.original) }}
        >
          <Text transform='uppercase'>{row.original.isOneTimeUse ? 'one-time-use' : 'unlimited-uses'}</Text>
        </Badge>
      ),
    },

    {
      Header: 'Usage',
      accessor: 'totalUses',
      Cell: ({ row }: CellProps<DiscountListRow>) => (
        <Text sx={{ opacity: getOpacity(row.original) }}>
          <Group spacing='xs'>
            <IconUsers size='12' color='#15B8FF' />
            <Text size='sm' color='#15B8FF'>
              {row.original.totalUses ?? 0}
            </Text>
          </Group>
        </Text>
      ),
    },
    {
      Header: 'Status',
      id: 'status',
      Cell: ({ row }: CellProps<DiscountListRow>) => {
        if (row.original.deletedAt) {
          return <Badge color='gray'>Archived</Badge>;
        }
        return (
          <Badge color={row.original.isActive ? 'green' : 'yellow'} variant='light'>
            {row.original.isActive ? 'Active' : 'Inactive'}
          </Badge>
        );
      },
    },
    {
      id: 'action',
      disableSortBy: true,
      Cell: ({ row }: CellProps<DiscountListRow>) => {
        const isArchived = !!row.original.deletedAt;
        return (
          <Group position='right' px='xs'>
            <Menu
              position='bottom-end'
              items={[
                {
                  title: 'Edit',
                  icon: <IconPencil size={theme.fontSizes.sm} />,
                  onClick: () => handleEdit(row.original),
                  invisible: !permission,
                },
                {
                  title: 'Usage Report',
                  icon: <IconChartBar size={theme.fontSizes.sm} />,
                  onClick: () => handleOpenReport(row.original),
                },
                {
                  title: 'Archive',
                  icon: <IconArchive size={theme.fontSizes.sm} />,
                  onClick: () => handleArchive(row.original),
                  invisible: isArchived || !permission,
                },
                {
                  title: 'Unarchive',
                  icon: <IconArchiveOff size={theme.fontSizes.sm} />,
                  onClick: () => handleUnarchive(row.original),
                  invisible: !isArchived || !permission,
                },
              ]}
            />
          </Group>
        );
      },
      minWidth: '50px',
      maxWidth: '50px',
      sticky: true,
    },
  ];

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data]);

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['name', 'totalUses']}
        handleHeaderClick={handleHeaderClick}
        ordering={ordering}
        ascending={ascending}
      />
      <DrawerDiscountForm
        discountId={selectedDiscount?.discountId}
        opened={isEditFormOpen}
        closeHandler={handleCloseEditForm}
        formId='edit-discount-form'
        title={<Title order={3}>{permission ? 'Edit' : 'View'} Discount</Title>}
        type='edit'
        permission={permission}
      />
      <DrawerDiscountUsageReport
        opened={isReportOpen}
        closeHandler={handleCloseReport}
        discount={selectedReportDiscount}
      />
    </>
  );
};

export default DiscountsTableContainer;
